import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { UserDetailUiContext } from '../../shared/contexts/user-detail-ui.context';
import { Global360ViewContext } from '../../../shared/contexts/global-360-view.context';
import { ActivatedRoute, ActivationStart, Router, Scroll } from '@angular/router';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { MenuItem } from 'primeng/api';
import { get } from 'lodash';
import { combineLatest, of, Subscription, switchMap } from 'rxjs';
import { SiteContext } from '../../shared/contexts/site.context';
import { CiscoTileConfigContext } from '../../shared/contexts/cisco-tile-config.context';
import { SiteSummaryContext } from '../../../shared/contexts/site-summary.context';

@Component({
  selector: 'ziro-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
  providers: [UserDetailUiContext, Global360ViewContext, SiteContext],
  standalone: false,
})
export class UserDetailComponent implements OnInit, OnDestroy {
  private breadcrumbsService = inject(BreadcrumbsService);
  private userDetailUiContext = inject(UserDetailUiContext);
  private global360ViewContext = inject(Global360ViewContext);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private tileConfigContext = inject(CiscoTileConfigContext);
  private siteContext = inject(SiteContext);
  private _siteSummaryContext = inject(SiteSummaryContext);

  isLoading = true;
  username: string;
  baseCrumb: MenuItem;
  hideBreadcrumb = false;
  siteId: number;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.username = this.route.snapshot.paramMap.get('username');
    this.baseCrumb = {
      label: this.username,
      url: `/app2/#/user/${encodeURIComponent(this.username)}`,
    };
    this._updateBreadcrumbs(this.route.firstChild.snapshot.data.breadcrumb);

    const contextSubs = combineLatest([
      this.siteContext.state$,
      this._siteSummaryContext.state$,
      this.global360ViewContext.state$,
    ])
      .pipe(
        switchMap(([site, siteSummary, global360View]) => {
          const siteIdFromContext = Number(site.id);
          if (this.siteId !== siteIdFromContext) {
            this.siteId = siteIdFromContext;
            const cucmServerId = this._siteSummaryContext.findCucmServerIdForSite(siteSummary, this.siteId);
            const primaryExtension = global360View.primaryExtensions.find((ext) => ext.serverId === cucmServerId);
            return this.tileConfigContext.getTileConfigs(siteIdFromContext, primaryExtension?.extension);
          }
          return of(null);
        }),
      )
      .subscribe(() => {
        this.isLoading = false;
      });

    this._subscriptions.add(contextSubs);

    this.global360ViewContext.init(this.username);

    const routerSub = this.router.events.subscribe((event) => {
      if (event instanceof Scroll && event.routerEvent.url.includes('reassign')) {
        const reassignCrumb: MenuItem = {
          label: 'tkey;userdetail.breadcrumb.transfer.text',
          url: this.baseCrumb.url + '/reassign',
        };
        const crumbs = [this.baseCrumb, reassignCrumb];

        if (event.routerEvent.url.includes('/reassign/')) {
          const username = event.routerEvent.url.split('/').reverse()[0];
          const userCrumb: MenuItem = {
            label: username,
          };
          this.breadcrumbsService.updateBreadcrumbs(crumbs.concat(userCrumb));
        } else {
          this.breadcrumbsService.updateBreadcrumbs(crumbs);
        }
        return;
      }
      if (event instanceof ActivationStart) {
        const routeCrumb = get(event, ['snapshot', 'data', 'breadcrumb']);
        this._updateBreadcrumbs(routeCrumb);
      }
    });
    this._subscriptions.add(routerSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _updateBreadcrumbs(crumb: string): void {
    this.hideBreadcrumb = crumb === 'tkey;userdetail.breadcrumb.home.text';
    this.breadcrumbsService.updateBreadcrumbs([this.baseCrumb, { label: crumb }]);
  }
}
